import type {
  AreaUnit,
  CharacterCode,
  CsvColumnSettingMethod,
  EligibleBillingProvider,
  EntryDateSelection,
  FaApplicationType,
  ImportType,
  LaApplicationType,
  MstOrderItem,
  NumberingMethod,
  Order,
  OutputFileFormat,
  QuantityUnit,
  RegistrationMethod,
  SpecificationMethod,
} from '@libs/enums';
import type {
  DateRangeObject,
  ItemAttributes,
  ItemNames,
  OrEmpty,
  OutputKeyObject,
  StringRangeObject,
  WithBlankSearchObject,
} from '@ps-smart-cp/ppfw-client';
import {
  Bool,
  Characters,
  DateRange,
  ItemName,
  Locale,
  Numeric,
  OutputKey,
  SingleDate,
  SingleValue,
  StringRange,
  UploadFile,
  ValueSet,
  WithBlankSearch,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  id: number;
  version: number;
  /**
   * 運用開始制御
   */
  isStarted: boolean;
  /**
   * 数量
   */
  quantity: string;
  /**
   * 備考
   */
  note: string;
  /**
   * 備考1
   */
  noteOne: string;
  /**
   * 備考2
   */
  noteTwo: string;
  /**
   * 計上年月日
   */
  entryDate: Date | null;
  /**
   * 計上年月日(抽出条件)
   */
  entryDateCondition: DateRangeObject;
  /**
   * 計上年月日(テキスト)
   */
  rawEntryDate: string;
  /**
   * 計上月度
   */
  entryMonth: string;
  /**
   * 特記事項1
   */
  specialItemOne: string;
  /**
   * 特記事項2
   */
  specialItemTwo: string;
  /**
   * 稟議決裁番号
   */
  approvalNo: string;
  /**
   * 稟議決裁番号(抽出条件)
   */
  approvalNoCondition: WithBlankSearchObject;
  /**
   * 自動採番機能
   */
  autoNumberingFunc: boolean;
  /**
   * 摘要
   */
  summary: string;
  /**
   * 摘要(抽出条件)
   */
  summaryCondition: WithBlankSearchObject;
  /**
   * ヘッダ行の設定
   */
  csvColumnSettingMethod: CsvColumnSettingMethod;
  //TODO PPBOS-3278 commentに変更するか検討
  /**
   * コメント
   */
  jobComment: string;
  /**
   * コメント(抽出条件)
   */
  jobCommentCondition: WithBlankSearchObject;
  /**
   * 採番方法区分
   */
  numberingMethodClass: NumberingMethod;
  /**
   * 電話番号
   */
  phoneNo: string;
  /**
   * 適格請求書発行事業者
   */
  eligibleBillingProvider: boolean;
  /**
   * 適格請求書発行事業者
   */
  eligibleBillingProviderCondition: Set<EligibleBillingProvider>;
  /**
   * 数量単位
   */
  quantityUnit: OrEmpty<QuantityUnit>;
  /**
   * 数量単位(テキスト)
   */
  rawQuantityUnit: string;
  /**
   * 面積単位
   */
  areaUnit: OrEmpty<AreaUnit>;
  /**
   * 住所
   */
  address: string;
  /**
   * 使用有無
   */
  isUsed: boolean;
  /**
   * 備考(抽出条件)
   */
  noteCondition: WithBlankSearchObject;
  /**
   * 項目順
   */
  orderItem: MstOrderItem;
  /**
   * 順序
   */
  order: Order;
  /**
   * 出力ファイル形式
   */
  outputFileFormat: Set<OutputFileFormat>;
  /**
   * ファイル
   */
  file: File | null;
  /**
   * ファイル種類
   */
  fileType: string;
  /**
   * 取込区分
   */
  importClass: OrEmpty<ImportType>;
  /**
   * 文字コード
   */
  characterCode: OrEmpty<CharacterCode>;
  /**
   * 変更年月日
   */
  changeDate: Date | null;
  /**
   * 変更年月日(テキスト)
   */
  rawChangeDate: string;
  /**
   * 出力年月
   */
  outputYearMonth: Date | null;
  /**
   * 入力補助を使用する
   */
  useInputSupport: boolean;
  /**
   * 郵便番号
   */
  postalCode: string;
  /**
   * 住所1
   */
  address01: string;
  /**
   * 住所2
   */
  address02: string;
  /**
   * 電話番号1
   */
  phoneNo01: string;
  /**
   * 電話番号2
   */
  phoneNo02: string;
  /**
   * 運用開始判定用
   */
  operationStartFlg: boolean;
  /**
   * 期首年月
   */
  beginningYm: Date | null;
  /**
   * 申請種別
   */
  readonly applicationClass: string;
  /**
   * 申請種別(抽出条件)
   */
  readonly applicationClassCondition: OrEmpty<FaApplicationType>;
  /**
   * 入力年月日
   */
  readonly inputDateCondition: DateRangeObject;
  /**
   * 入力者
   */
  readonly inputUserCondition: WithBlankSearchObject;
  /**
   * 最終修正者
   */
  readonly lastUpdateUserCondition: WithBlankSearchObject;
  /**
   * LA申請種別
   */
  laApplicationClass: OrEmpty<LaApplicationType>;
  /**
   * LA申請種別(抽出条件)
   */
  laApplicationClassCondition: OrEmpty<LaApplicationType>;
  /**
   * 面積
   */
  area: string;
  /**
   * 入力年月日
   */
  inputDate: Date | null;
  /**
   * 登録方法区分
   */
  registrationMethodClass: OrEmpty<RegistrationMethod>;
  /**
   * 入力者
   */
  inputUser: string;
  /**
   * 入力値
   */
  inputValue: string;
  /**
   * 最終修正者
   */
  lastUpdateUser: string;
  /**
   * 最終修正年月日
   */
  lastUpdateDate: Date | null;
  /**
   * 事由
   */
  reason: string;
  /**
   * 登録年月日
   */
  registrationDate: Date | null;
  /**
   * 理論値
   */
  theoreticalValue: string;
  /**
   * 入力指定(抽出条件)
   */
  specifyInputCondition: DateRangeObject;
  /**
   * 選択指定(抽出条件)
   */
  specifySelectionCondition: OrEmpty<EntryDateSelection>;
  /**
   * 集計単位(抽出条件)
   */
  totalUnitCondition: SpecificationMethod;
  /**
   * 更新番号・親(抽出条件)
   */
  updateNoParentRange: StringRangeObject;
  /**
   * 備考1(抽出条件)
   */
  noteOneCondition: WithBlankSearchObject;
  /**
   * 備考2(抽出条件)
   */
  noteTwoCondition: WithBlankSearchObject;
  /**
   * 特記事項1(抽出条件)
   */
  specialItemOneCondition: WithBlankSearchObject;
  /**
   * 特記事項2(抽出条件)
   */
  specialItemTwoCondition: WithBlankSearchObject;
  /**
   * 出力キー
   */
  outputKey: string;
  /**
   * 第一出力キー
   */
  outputKeyOne: string;
  /**
   * 第二出力キー
   */
  outputKeyTwo: string;
  /**
   * 第三出力キー
   */
  outputKeyThree: string;
  /**
   * 第四出力キー
   */
  outputKeyFour: string;
  /**
   * 第五出力キー
   */
  outputKeyFive: string;
  /**
   * 第一出力順
   */
  outputOrderOne: OutputKeyObject;
  /**
   * 第二出力順
   */
  outputOrderTwo: OutputKeyObject;
  /**
   * 第三出力順
   */
  outputOrderThree: OutputKeyObject;
  /**
   * 第四出力順
   */
  outputOrderFour: OutputKeyObject;
  /**
   * 第五出力順
   */
  outputOrderFive: OutputKeyObject;
  /**
   * 第一出力キー
   */
  outputGroupKey1: string;
  /**
   * 第二出力キー
   */
  outputGroupKey2: string;
  /**
   * 第三出力キー
   */
  outputGroupKey3: string;
  /**
   * 第四出力キー
   */
  outputGroupKey4: string;
  /**
   * 第五出力キー
   */
  outputGroupKey5: string;
  /**
   * 第一出力順
   */
  outputOrderKey1: OutputKeyObject;
  /**
   * 第二出力順
   */
  outputOrderKey2: OutputKeyObject;
  /**
   * 第三出力順
   */
  outputOrderKey3: OutputKeyObject;
  /**
   * 第四出力順
   */
  outputOrderKey4: OutputKeyObject;
  /**
   * 第五出力順
   */
  outputOrderKey5: OutputKeyObject;
  /**
   * 備考1
   */
  note01: string;
  /**
   * 備考2
   */
  note02: string;
  /**
   * 特記事項1
   */
  specialItem01: string;
  /**
   * 特記事項2
   */
  specialItem02: string;
  /**
   * キー
   */
  key: string;
  /**
   * 指定方法
   */
  specificationMethod: string;
}

const attributes = {
  quantity: Numeric.of(0, 99_999, 0),
  note: Characters.preset.freeFormat(),
  noteCondition: WithBlankSearch.of(Characters.preset.freeFormat()),
  noteOne: Characters.preset.freeFormat({ maxLen: 60 }),
  noteTwo: Characters.preset.freeFormat({ maxLen: 60 }),
  entryDate: SingleDate.ymd(true),
  entryDateCondition: DateRange.ymd(),
  entryMonth: SingleDate.ym(),
  approvalNo: Characters.preset.alphanumericals({ maxLen: 20 }),
  approvalNoCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 20 })),
  autoNumberingFunc: Bool.new(),
  summary: Characters.preset.freeFormat({ maxLen: 50 }),
  summaryCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 50 })),
  csvColumnSettingMethod: SingleValue.shortValue(),
  jobComment: Characters.preset.freeFormat(),
  jobCommentCondition: WithBlankSearch.of(Characters.preset.freeFormat()),
  numberingMethodClass: SingleValue.shortValue(),
  phoneNo: Characters.preset.freeFormat({ maxLen: 15 }),
  eligibleBillingProvider: Bool.new(),
  eligibleBillingProviderCondition: ValueSet.new(),
  quantityUnit: SingleValue.shortValue(),
  areaUnit: SingleValue.shortValue(true),
  address: Characters.preset.freeFormat(),
  isUsed: Bool.new(),
  orderItem: SingleValue.shortValue(true),
  order: SingleValue.shortValue(true),
  outputFileFormat: ValueSet.new(),
  changeDate: SingleDate.ymd(true),
  outputYearMonth: SingleDate.ym(true),
  characterCode: SingleValue.shortValue(true),
  file: UploadFile.of({ required: true, accept: ['csv'] }),
  importClass: SingleValue.shortValue(true),
  useInputSupport: Bool.new(),
  address01: Characters.preset.freeFormat(),
  address02: Characters.preset.freeFormat(),
  phoneNo01: Characters.preset.halfWidth({ maxLen: 15 }),
  phoneNo02: Characters.preset.halfWidth({ maxLen: 15 }),
  postalCode: Characters.preset.halfWidth({ maxLen: 7, pad: { position: 'start', with: '0' } }),
  operationStartFlg: Bool.new(),
  beginningYm: SingleDate.ym(true),
  applicationClass: SingleValue.shortValue(),
  applicationClassCondition: SingleValue.shortValue(),
  inputDateCondition: DateRange.ymd(),
  lastUpdateUserCondition: WithBlankSearch.of(Characters.preset.freeFormat()),
  inputUserCondition: WithBlankSearch.of(Characters.preset.freeFormat()),
  laApplicationClassCondition: SingleValue.shortValue(),
  area: Numeric.of(0, 999_999_9, 2),
  specifyInputCondition: DateRange.ym(),
  specifySelectionCondition: SingleValue.longValue(),
  totalUnitCondition: SingleValue.longValue(true),
  updateNoParentRange: StringRange.of(
    Characters.preset.numerical({ minLen: 15, maxLen: 15 }),
    Characters.preset.numerical({ minLen: 15, maxLen: 15 }),
  ),
  noteOneCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 60 })),
  noteTwoCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 60 })),
  specialItemOneCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 500 })),
  specialItemTwoCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 500 })),
  outputKey: SingleValue.shortValue(),
  outputKeyOne: SingleValue.shortValue(),
  outputOrderOne: OutputKey.of(SingleValue.shortValue(), SingleValue.longValue()),
  outputGroupKey1: SingleValue.shortValue(),
  outputOrderKey1: OutputKey.of(SingleValue.shortValue(), SingleValue.longValue()),
  note01: Characters.preset.freeFormat({ maxLen: 60 }),
  note02: Characters.preset.freeFormat({ maxLen: 60 }),
  specialItem01: Characters.preset.freeFormat({ maxLen: 256 }),
  specialItem02: Characters.preset.freeFormat({ maxLen: 256 }),
} satisfies ItemAttributes<Schema>;

const names = {
  quantity: ItemName.of([Locale.ja, '数量']),
  note: ItemName.of([Locale.ja, '備考'], [Locale.en, 'Note']),
  noteOne: ItemName.of([Locale.ja, '備考1'], [Locale.en, 'Note One']),
  noteTwo: ItemName.of([Locale.ja, '備考2'], [Locale.en, 'Note Two']),
  noteCondition: ItemName.of([Locale.ja, '備考'], [Locale.en, 'Note']),
  entryDate: ItemName.of([Locale.ja, '計上年月日'], [Locale.en, 'Entry Date']),
  entryDateCondition: ItemName.of([Locale.ja, '計上年月日'], [Locale.en, 'Entry Date']),
  entryMonth: ItemName.of([Locale.ja, '計上月度'], [Locale.en, 'Entry Month']),
  specialItemOne: ItemName.of([Locale.ja, '特記事項1'], [Locale.en, 'Special Item One']),
  specialItemTwo: ItemName.of([Locale.ja, '特記事項2'], [Locale.en, 'Special Item Two']),
  approvalNo: ItemName.of([Locale.ja, '稟議決裁番号'], [Locale.en, 'Approval No']),
  approvalNoCondition: ItemName.of([Locale.ja, '稟議決裁番号'], [Locale.en, 'Approval No']),
  autoNumberingFunc: ItemName.of([Locale.ja, '自動採番機能'], [Locale.en, 'Auto Number Func']),
  summary: ItemName.of([Locale.ja, '摘要'], [Locale.en, 'Summary']),
  summaryCondition: ItemName.of([Locale.ja, '摘要'], [Locale.en, 'Summary']),
  csvColumnSettingMethod: ItemName.of([Locale.ja, 'ヘッダ行の設定']),
  jobComment: ItemName.of([Locale.ja, 'コメント'], [Locale.en, 'Job Comment']),
  jobCommentCondition: ItemName.of([Locale.ja, 'コメント'], [Locale.en, 'Job Comment']),
  numberingMethodClass: ItemName.of([Locale.ja, '採番方法区分'], [Locale.en, 'Numbering Method Class']),
  phoneNo: ItemName.of([Locale.ja, '電話番号'], [Locale.en, 'Phone Number']),
  eligibleBillingProvider: ItemName.of([Locale.ja, '適格請求書発行事業者']),
  eligibleBillingProviderCondition: ItemName.of([Locale.ja, '適格請求書発行事業者']),
  quantityUnit: ItemName.of([Locale.ja, '数量単位']),
  areaUnit: ItemName.of([Locale.ja, '面積単位']),
  address: ItemName.of([Locale.ja, '住所']),
  isUsed: ItemName.of([Locale.ja, '使用有無'], [Locale.en, 'Is Used']),
  orderItem: ItemName.of([Locale.ja, '項目名']),
  order: ItemName.of([Locale.ja, '順序']),
  outputFileFormat: ItemName.of([Locale.ja, '出力ファイル形式']),
  changeDate: ItemName.of([Locale.ja, '変更年月日']),
  outputYearMonth: ItemName.of([Locale.ja, '出力年月']),
  file: ItemName.of([Locale.ja, 'ファイル']),
  fileType: ItemName.of([Locale.ja, 'ファイル種類'], [Locale.en, 'File Type']),
  importClass: ItemName.of([Locale.ja, '取込区分']),
  characterCode: ItemName.of([Locale.ja, '文字コード']),
  useInputSupport: ItemName.of([Locale.ja, '入力補助を使用する']),
  postalCode: ItemName.of([Locale.ja, '郵便番号'], [Locale.en, 'Postal Code']),
  address01: ItemName.of([Locale.ja, '住所1'], [Locale.en, 'Address One']),
  address02: ItemName.of([Locale.ja, '住所2'], [Locale.en, 'Address Two']),
  phoneNo01: ItemName.of([Locale.ja, '電話番号1'], [Locale.en, 'phone Number One']),
  phoneNo02: ItemName.of([Locale.ja, '電話番号2'], [Locale.en, 'phone Number Two']),
  operationStartFlg: ItemName.of([Locale.ja, '運用開始判定用'], [Locale.en, 'Operation Start Flg']),
  beginningYm: ItemName.of([Locale.ja, '期首年月'], [Locale.en, 'Begin Term Date']),
  applicationClass: ItemName.of([Locale.ja, '申請種別'], [Locale.en, 'Application Classification']),
  inputDateCondition: ItemName.of([Locale.ja, '入力年月日'], [Locale.en, 'Input Date']),
  lastUpdateUserCondition: ItemName.of([Locale.ja, '最終修正者'], [Locale.en, 'Last Update User']),
  inputUserCondition: ItemName.of([Locale.ja, '入力者'], [Locale.en, 'Input User']),
  laApplicationClass: ItemName.of([Locale.ja, '申請種別'], [Locale.en, 'La Application Classification']),
  area: ItemName.of([Locale.ja, '面積']),
  applicationClassCondition: ItemName.of([Locale.ja, '申請種別'], [Locale.en, 'Application Classification']),
  laApplicationClassCondition: ItemName.of(
    [Locale.ja, '申請種別'],
    [Locale.en, 'La Application Classification'],
  ),
  inputDate: ItemName.of([Locale.ja, '入力年月日'], [Locale.en, 'Input Date']),
  inputUser: ItemName.of([Locale.ja, '入力者'], [Locale.en, 'Input User']),
  inputValue: ItemName.of([Locale.ja, '入力値']),
  registrationMethodClass: ItemName.of([Locale.ja, '登録方法区分'], [Locale.en, 'Registration Method Class']),
  lastUpdateDate: ItemName.of([Locale.ja, '最終修正年月日'], [Locale.en, 'Last Update Date']),
  lastUpdateUser: ItemName.of([Locale.ja, '最終修正者'], [Locale.en, 'Last Update User']),
  reason: ItemName.of([Locale.ja, '事由'], [Locale.en, 'Reason']),
  registrationDate: ItemName.of([Locale.ja, '登録年月日']),
  specifySelectionCondition: ItemName.of([Locale.ja, '選択指定']),
  specifyInputCondition: ItemName.of([Locale.ja, '入力指定']),
  theoreticalValue: ItemName.of([Locale.ja, '理論値']),
  totalUnitCondition: ItemName.of([Locale.ja, '集計単位']),
  noteOneCondition: ItemName.of([Locale.ja, '備考1']),
  noteTwoCondition: ItemName.of([Locale.ja, '備考2']),
  specialItemOneCondition: ItemName.of([Locale.ja, '特記事項1']),
  specialItemTwoCondition: ItemName.of([Locale.ja, '特記事項2']),
  note01: ItemName.of([Locale.ja, '備考1']),
  note02: ItemName.of([Locale.ja, '備考2']),
  specialItem01: ItemName.of([Locale.ja, '特記事項1']),
  specialItem02: ItemName.of([Locale.ja, '特記事項2']),
  specificationMethod: ItemName.of([Locale.ja, '指定方法']),
} satisfies ItemNames<Schema>;

const labelNames = {
  eligibleBillingProvider: ItemName.of([Locale.ja, '適格請求書発行事業者の対象']),
  outputOrder: ItemName.of([Locale.ja, '出力順']),
  totalUnit: ItemName.of([Locale.ja, '集計単位']),
  specifySelection: ItemName.of([Locale.ja, '選択指定']),
  specifyInput: ItemName.of([Locale.ja, '入力指定']),
  range: ItemName.of([Locale.ja, '範囲']),
  acquiredApprovalNo: ItemName.of([Locale.ja, '取得時稟議決裁番号']),
  acquiredSummary: ItemName.of([Locale.ja, '取得時摘要']),
  outputKey: ItemName.of([Locale.ja, '出力キー']),
  outputKeyOne: ItemName.of([Locale.ja, '第1キー']),
  outputKeyTwo: ItemName.of([Locale.ja, '第2キー']),
  outputKeyThree: ItemName.of([Locale.ja, '第3キー']),
  outputKeyFour: ItemName.of([Locale.ja, '第4キー']),
  outputKeyFive: ItemName.of([Locale.ja, '第5キー']),
  outputGroupKey1: ItemName.of([Locale.ja, '第1キー']),
  outputGroupKey2: ItemName.of([Locale.ja, '第2キー']),
  outputGroupKey3: ItemName.of([Locale.ja, '第3キー']),
  outputGroupKey4: ItemName.of([Locale.ja, '第4キー']),
  outputGroupKey5: ItemName.of([Locale.ja, '第5キー']),
};

interface Record {
  id: number;
  version: number;
  isStarted: boolean;
  quantity: string;
  entryDateFrom: string;
  entryDateTo: string;
  note: string;
  noteOne: string;
  noteTwo: string;
  entryDate: string;
  entryMonth: string;
  specialItemOne: string;
  specialItemTwo: string;
  approvalNo: string;
  approvalNoCondition: string;
  isApprovalNoBlankOnly: boolean;
  autoNumberingFunc: boolean;
  summary: string;
  isSummaryBlankOnly: boolean;
  csvColumnSettingMethod: string;
  jobComment: string;
  jobCommentCondition: string;
  isJobCommentConditionBlankOnly: boolean;
  numberingMethodClass: string;
  phoneNo: string;
  eligibleBillingProvider: boolean;
  eligibleBillingProviderCondition: string[];
  quantityUnit: string;
  areaUnit: string;
  address: string;
  isUsed: boolean;
  orderItem: string;
  order: string;
  outputFileFormat: string[];
  changeDate: string;
  file: File | string | null;
  importClass: string;
  characterCode: string;
  useInputSupport: boolean;
  postalCode: string;
  address01: string;
  address02: string;
  phoneNo01: string;
  phoneNo02: string;
  operationStartFlg: boolean;
  beginningYm: string;
  applicationClass: string;
  inputDateFrom: string;
  inputDateTo: string;
  inputUser: string;
  isInputUserBlankOnly: boolean;
  lastUpdateUser: string;
  isLastUpdateUserBlankOnly: boolean;
  laApplicationClass: string;
  area: string;
  registrationMethodClass: string;
  lastUpdateDate: string;
  reason: string;
  inputDate: string;
  inputValue: string;
  registrationDate: string;
  theoreticalValue: string;
  specifyInputConditionFrom: string;
  specifyInputConditionTo: string;
  specifySelectionCondition: string;
  totalUnitCondition: string;
  outputYearMonth: string;
  isNoteBlankOnly: boolean;
  isNoteOneBlankOnly: boolean;
  isNoteTwoBlankOnly: boolean;
  isSpecialItemOneBlankOnly: boolean;
  isSpecialItemTwoBlankOnly: boolean;
  outputKey: string;
  outputKeyOne: string;
  outputKeyTwo: string;
  outputKeyThree: string;
  outputKeyFour: string;
  outputKeyFive: string;
  outputOrderItemOne: string;
  outputOrderItemTwo: string;
  outputOrderItemThree: string;
  outputOrderItemFour: string;
  outputOrderItemFive: string;
  outputOrderOne: string;
  outputOrderTwo: string;
  outputOrderThree: string;
  outputOrderFour: string;
  outputOrderFive: string;
  outputGroupKey1: string;
  outputGroupKey2: string;
  outputGroupKey3: string;
  outputGroupKey4: string;
  outputGroupKey5: string;
  outputOrderKey1: OutputKeyObject;
  outputOrderKey2: OutputKeyObject;
  outputOrderKey3: OutputKeyObject;
  outputOrderKey4: OutputKeyObject;
  outputOrderKey5: OutputKeyObject;
  spareName01: string;
  spareName02: string;
  note01: string;
  note02: string;
  specialItem01: string;
  specialItem02: string;
  specifyInputYmFrom: string;
  specifyInputYmTo: string;
}

const common = {
  labelNames,
  names,
  attributes,
};

interface Common {
  schema: Schema;
  record: Record;
}

export type { Common };
export { common };
